
import { faFileDownload, faFilter, faXmark } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import Pagination from 'components/pagination/Pagination';
import Spinner from 'components/spinner/Spinner';
import { IBodyResponseOrdersListSum, IDetailsOrdersList } from 'interfaces/remaining';
import React, { useEffect, useMemo, useState, lazy, Suspense } from 'react';

import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { Column, useExpanded, useTable } from 'react-table';
import { RootState } from 'store/store';
import useHttp from 'utils/hooks/useHttp';
import './EspaceAutomotorRemainingV2.scss';
import { columnOrders } from './ColumnTable/columnTable';
import i18n from 'i18n';
import Filter from './Filter/filter';
import { isEmpty } from 'lodash';
import { useDeviceDetect } from 'utils/hooks/deviceDetect';

const ItemOrderList = lazy(() => import('./ItemOrderList/ItemOrderList'));

const EspaceAutomotorRemainingV2: React.FC = () => {

  const { t } = useTranslation();
  const http = useHttp();
  const [data, setData] = React.useState<IDetailsOrdersList[]>([]);
  const [sum, setSum] = React.useState<IBodyResponseOrdersListSum[]>([]);
  const [loading, setLoading] = useState(false);
  const [loadingContent, setLoadingContent] = useState(false);
  const [pageCount, setPageCount] = useState(0);
  const { isMobile, isTablet, isDesktop } = useDeviceDetect(768, 1200);
  const [filter, setFilter] = useState<{}>({});
  const [isActive, setIsActive] = useState(false);
  const { user } = useSelector((state: RootState) => state.userData);
  const [loadingExportMobile, setLoadingExportMobile] = useState<boolean>(false);
  const [forcePage, setForcePage] = useState<number>(1);

  const request = (params) => {

    if (data.length === 0) {
      setLoading(true);
    } else {

      setLoadingContent(true);
    }

    http.remainingHttp.getListOrders(params).then((res) => {
      setData(res.data.data);
      setPageCount(res.data.total_pages);

    }).finally(() => {

      setLoading(false);
      setLoadingContent(false);

    });
  };

  useEffect(() => {

    http.remainingHttp.getListOrdersSum(filter).then((res) => {
      setSum([res.data]);
    });

  }, [filter]);


  useEffect(() => {
    const params = { ...filter, ...{ page: forcePage, per_page: 10 } };

    request(params);

  }, [filter, forcePage]);


  const handlePageClick = (page: number) => {

    setForcePage(page);

    const params = { ...filter, ...{ page, per_page: 10 } };

    request(params);

  };


  let hiddenColumns = isTablet ? ['quantity_invoiced', 'quantity_to_invoiced', 'quantity_not_received'] : [];

  if (isMobile) {
    hiddenColumns = [
      'quantity',
      'quantity_invoiced',
      'total_price_invoiced',
      'quantity_to_invoiced',
      'total_price_to_invoiced',
      'quantity_not_received',
      'total_price_not_received',
      'date'
    ];
  }


  const initialState = hiddenColumns ? {
    'hiddenColumns': hiddenColumns
  } : {};

  const columnsTable = useMemo(
    (): Column<IDetailsOrdersList>[] => columnOrders(), [user, i18n.language, data, isMobile, isTablet, isDesktop]
  );


  const { getTableProps, getTableBodyProps, headerGroups, rows, prepareRow, footerGroups } = useTable({
    columns: columnsTable,
    data: data,
    sum: sum,
    initialState
  }, useExpanded);


  return (
    <div className="form-container-section-account-remaining-v2">
      <div className="container-section-account-remaining">

        <div className="title">{t('_order_availability').toLocaleUpperCase()}</div>

        {isMobile && (
          <div className="filter-btns">
            <button className="button-filter-remaining" onClick={() => setIsActive(true)}>
              <FontAwesomeIcon icon={faFilter} /> {t('_filter')}
            </button>
            <button className="export-btn" onClick={() => setLoadingExportMobile(true)} disabled={loadingExportMobile}>
              {loadingExportMobile ? <Spinner class="extra-small" /> : <FontAwesomeIcon icon={faFileDownload} className="icon" />} {t('_export')}
            </button>
          </div>
        )}

        <div className={`remaining-filter ${isMobile && !isActive ? 'hidden-filter-remaining' : 'show-filter-remaining'}`}>
          <Filter
            setIsActive={setIsActive}
            setLoadingExportMobile={setLoadingExportMobile}
            loadingExportMobile={loadingExportMobile}
            setForcePage={setForcePage}
            setFilter={setFilter}
          />
        </div>

        <div className="table-wrapper">
          {loading ? (
            <Spinner />
          ) : (
            <table {...getTableProps()}>

              <thead className="thead">
                {headerGroups.map((headerGroup, hgIndex) => (
                  <tr {...headerGroup.getHeaderGroupProps()} key={hgIndex} className="row">
                    {headerGroup.headers.map((column, colIndex) => (
                      <th
                        {...column.getHeaderProps({ style: { minWidth: column.minWidth, width: column.width, maxWidth: column.maxWidth } })}
                        key={colIndex}
                        className="heading"
                      >
                        <div className="table-title">{column.render('Header')}</div>
                      </th>
                    ))}
                  </tr>
                ))}
              </thead>

              {loadingContent && data.length ? (
                <tbody>
                  <tr>
                    <td colSpan={columnsTable.length}>
                      <Spinner class="extra-small" />
                    </td>
                  </tr>
                </tbody>
              ) : (
                <tbody {...getTableBodyProps()} className="body">
                  {data.length ? (
                    rows.map((row) => {
                      prepareRow(row);
                      const { key, ...restRowProps } = row.getRowProps();

                      return (
                        <React.Fragment key={key}>
                          <tr {...restRowProps} className="body-row-espace">
                            {row.cells.map((cell) => {
                              const { key, ...restCellProps } = cell.getCellProps();

                              return (
                                <td key={key} {...restCellProps} className="cell">
                                  {cell.render('Cell')}
                                </td>
                              );
                            })}
                          </tr>

                          {row.isExpanded && (
                            <tr className="expanded-row">
                              <td colSpan={row.cells.length}>
                                <Suspense fallback={<Spinner class="extra-small" />}>
                                  <ItemOrderList orderId={row.original.order_id} />
                                </Suspense>
                              </td>
                            </tr>
                          )}
                        </React.Fragment>
                      );
                    })
                  ) : (
                    <tr className="notFond">
                      <td colSpan={columnsTable.length}>{t('_no_data_found')}</td>
                    </tr>
                  )}
                </tbody>
              )}

              {!isEmpty(sum[0]?.total_price) && (
                <tfoot>
                  {footerGroups.map((headerGroup, hgIndex) => (
                    <tr {...headerGroup.getFooterGroupProps()} key={hgIndex} className="row">
                      {headerGroup.headers.map((column, colIndex) => (
                        <th {...column.getHeaderProps()} key={colIndex} className="heading">
                          <div className="table-title">{column.render('Footer')}</div>
                        </th>
                      ))}
                    </tr>
                  ))}
                </tfoot>
              )}

            </table>
          )}
        </div>

        {pageCount > 1 && <Pagination pageCount={pageCount} forcePage={forcePage} handlePageClick={handlePageClick} hiddenParamPage />}
      </div>
    </div>
  );

};

EspaceAutomotorRemainingV2.displayName = 'EspaceAutomotorRemainingV2';

export default EspaceAutomotorRemainingV2;
